/**
 * Display formatting.
 *
 * Rules that hold everywhere in this interface:
 *
 * - A token symbol is never invented. When metadata could not be read the UI
 *   says so and falls back to the address, because a placeholder like "???"
 *   next to a real symbol invites the operator to read it as a name.
 *
 * - Amounts arrive as decimal strings and are formatted from the string. They
 *   are never parsed into a float first: an 18-decimal value does not survive
 *   the round trip, and this is the number the operator compares against their
 *   wallet.
 */

/** Shorten an address for display, keeping enough to recognise it. */
export function shortAddress(address: string, lead = 6, tail = 4): string {
  if (address.length <= lead + tail + 2) return address;
  return `${address.slice(0, lead)}…${address.slice(-tail)}`;
}

export interface TokenLike {
  address: string;
  symbol: string | null;
  metadata_status: 'pending' | 'ok' | 'failed';
}

/**
 * How to label a token.
 *
 * Returns both the text and whether it is a real symbol, so callers can style
 * an unknown token differently rather than having it read as a name.
 */
export function tokenLabel(token: TokenLike): { text: string; isSymbol: boolean } {
  if (token.symbol && token.symbol.trim().length > 0) {
    return { text: token.symbol, isSymbol: true };
  }
  return { text: shortAddress(token.address), isSymbol: false };
}

/**
 * Trim trailing zeros from a decimal string without touching its value.
 *
 * "4.215000000000000000" becomes "4.215"; "1.000000" becomes "1". Purely
 * textual, so no precision is lost.
 */
export function trimDecimalString(value: string): string {
  if (!value.includes('.')) return value;
  const trimmed = value.replace(/0+$/, '').replace(/\.$/, '');
  return trimmed.length === 0 ? '0' : trimmed;
}

/**
 * Format an ETH amount for display.
 *
 * Caps at `maxDecimals` significant decimal places by truncating the string —
 * never by rounding a float. Truncation rather than rounding is deliberate: a
 * displayed balance should never appear larger than what is actually held.
 */
export function formatEth(value: string | null, maxDecimals = 4): string {
  if (value === null) return '—';

  const trimmed = trimDecimalString(value);
  const dot = trimmed.indexOf('.');
  if (dot === -1) return trimmed;

  const whole = trimmed.slice(0, dot);
  const fraction = trimmed.slice(dot + 1, dot + 1 + maxDecimals).replace(/0+$/, '');

  // A non-zero amount too small to show at this precision must NOT render as
  // "0". This is ADR-039's failure in the display layer: a pool holding
  // 0.000000156 ETH truncates to an all-zero fraction, which then gets stripped,
  // and the operator reads "0 ETH" — indistinguishable from an empty pool. The
  // comment below used to claim the ellipsis prevented that; it did not, because
  // the ellipsis was only appended after a surviving fraction.
  //
  // `<0.0001` is the honest rendering: smaller than this display can show, and
  // definitely not nothing.
  if (fraction.length === 0) {
    const isZero = /^0+$/.test(whole) && /^0*$/.test(trimmed.slice(dot + 1));
    if (isZero) return whole;
    return `<0.${'0'.repeat(maxDecimals - 1)}1`;
  }

  // Flag that the display is truncated, so a tiny non-zero amount does not read
  // as exactly zero.
  const truncated = trimmed.length > dot + 1 + maxDecimals;
  return `${whole}.${fraction}${truncated ? '…' : ''}`;
}

/** Format a token amount, allowing more decimals than an ETH value. */
export function formatTokenAmount(value: string): string {
  return formatEth(value, 6);
}

/** V3 fee tier in hundredths of a bip, as a percentage. */
export function formatFeeTier(feeTier: number | null): string | null {
  if (feeTier === null) return null;
  const percent = feeTier / 10_000;
  return `${trimDecimalString(percent.toFixed(4))}%`;
}

/**
 * Compact relative age, e.g. "2m", "3h", "5d".
 *
 * Age is the single most important fact about a new pair, so it is rendered
 * tersely enough to scan down a column.
 */
export function relativeAge(isoTimestamp: string, now: number = Date.now()): string {
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.max(0, Math.floor((now - then) / 1000));

  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 365) return `${days}d`;
  return `${Math.floor(days / 365)}y`;
}

/**
 * Insert thousands separators into an unsigned integer digit string, without
 * ever parsing it as a number. Purely textual, same discipline as
 * `trimDecimalString`.
 */
function addThousandsSeparators(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format a USD amount for display -- liquidity, FDV, market cap, volume.
 * Thousands-separated, truncated (never rounded) to `maxDecimals`, and
 * formatted directly from the string per this file's own rule: an amount is
 * never parsed into a float first.
 */
export function formatUsd(value: string | null, maxDecimals = 2): string {
  if (value === null) return '\u2014';

  const trimmed = trimDecimalString(value);
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction =
    maxDecimals > 0 && dot !== -1
      ? unsigned.slice(dot + 1, dot + 1 + maxDecimals).replace(/0+$/, '')
      : '';

  const wholeFormatted = addThousandsSeparators(whole);
  const sign = negative ? '-' : '';
  return fraction.length > 0 ? `${sign}$${wholeFormatted}.${fraction}` : `${sign}$${wholeFormatted}`;
}

/**
 * Format a USD price that may be far below one cent -- a brand-new token's
 * price is routinely `0.0000000091`. Reuses `formatEth`'s truncate-and-flag
 * logic, which already solves exactly this problem (a tiny non-zero value
 * must never display as "0"), and prefixes the `$` after the fact rather than
 * duplicating that logic.
 */
export function formatUsdPrice(value: string | null, maxDecimals = 6): string {
  if (value === null) return '\u2014';
  const formatted = formatEth(value, maxDecimals);
  if (formatted === '\u2014') return '\u2014';
  return formatted.startsWith('<') ? `<$${formatted.slice(1)}` : `$${formatted}`;
}

/**
 * Format a signed percentage, e.g. "+3.2%" / "-4.2%". Textual, from the
 * string, same truncation discipline as everywhere else in this file.
 */
export function formatPercent(value: string | null, maxDecimals = 2): string {
  if (value === null) return '\u2014';

  const trimmed = trimDecimalString(value);
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction =
    maxDecimals > 0 && dot !== -1
      ? unsigned.slice(dot + 1, dot + 1 + maxDecimals).replace(/0+$/, '')
      : '';
  const magnitude = fraction.length > 0 ? `${whole}.${fraction}` : whole;
  const isZero = /^0+$/.test(whole) && fraction.length === 0;
  const sign = negative ? '-' : isZero ? '' : '+';
  return `${sign}${magnitude}%`;
}

/** Absolute timestamp for a tooltip, where the exact moment matters. */
export function absoluteTime(isoTimestamp: string | null): string {
  if (isoTimestamp === null) return '—';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}
